/**
 * One place that turns "some coordinate system" into WGS84 lon/lat.
 *
 * WHY THIS IS SHARED
 * The shapefile reader had reprojection and the GeoJSON reader had none, so
 * the same file exported two ways behaved differently: the .shp was converted,
 * the .geojson was drawn wherever its metres happened to land. A projected
 * layer placed on a lon/lat map does not look broken — it looks like a layer
 * somewhere in the Atlantic — so this is the kind of bug a user reports as
 * "my data is missing" rather than "the projection is wrong".
 *
 * WHAT A CRS CAN ARRIVE AS
 *  - ESRI WKT, out of a shapefile's .prj                    → proj4 reads it
 *  - "EPSG:32643", or the OGC urn form, out of GeoJSON crs  → resolved below
 *  - nothing at all                                          → assumed lon/lat
 *
 * THE EPSG LIMIT, STATED PLAINLY
 * proj4 ships definitions for only a handful of codes. There is no EPSG
 * database in the browser and fetching one at import time would make a local
 * file import depend on a network call. So the codes resolved here are the
 * ones that can be DERIVED rather than looked up: the UTM families are
 * formulaic, and WGS84/Web Mercator are built in. Everything else is refused
 * with its code, which the user can act on — rather than guessed at, which
 * they cannot.
 */

import proj4 from 'proj4';

/** Converts one projected coordinate to [longitude, latitude]. */
export type Converter = (x: number, y: number) => [number, number];

export interface CrsResolution {
  /** Null when the data is already lon/lat and needs no conversion. */
  convert: Converter | null;
  /** How the CRS will be described in a notice. */
  name: string;
  /** Set when the CRS was named but could not be resolved. */
  error?: string;
}

/**
 * A proj4 definition for an EPSG code, where one can be derived.
 *
 * UTM is a family, not a list: zone N has its central meridian at
 * 6N - 183 degrees, and everything else about it is fixed. That makes 32601 to
 * 32660 and 32701 to 32760 — the codes almost every projected export in the
 * wild uses — resolvable without a database.
 */
function definitionForEpsg(code: number): string | null {
  if (code === 4326 || code === 4269 || code === 4258) return 'WGS84';
  if (code === 3857 || code === 900913 || code === 3785) {
    return (
      '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 ' +
      '+k=1 +units=m +nadgrids=@null +no_defs'
    );
  }

  // WGS84 / UTM northern and southern hemispheres.
  const utm = (zone: number, south: boolean) =>
    `+proj=utm +zone=${zone}${south ? ' +south' : ''} +datum=WGS84 +units=m +no_defs`;

  if (code >= 32601 && code <= 32660) return utm(code - 32600, false);
  if (code >= 32701 && code <= 32760) return utm(code - 32700, true);

  return null;
}

/** The EPSG number in any of the forms a file might name it. */
function epsgCodeFrom(identifier: string): number | null {
  const trimmed = identifier.trim();
  // "EPSG:32643", "urn:ogc:def:crs:EPSG::32643", "http://.../def/crs/EPSG/0/32643"
  const match =
    trimmed.match(/EPSG\s*:+\s*(\d+)/i) ??
    trimmed.match(/EPSG\/[\d.]*\/(\d+)/i) ??
    (/^\d+$/.test(trimmed) ? [trimmed, trimmed] : null);
  return match ? Number(match[1]) : null;
}

/** True when the identifier is already geographic degrees. */
function isGeographic(identifier: string): boolean {
  const code = epsgCodeFrom(identifier);
  if (code !== null) return code === 4326 || code === 4269 || code === 4258 || code === 84;
  // CRS84 is lon/lat with the axes in the order GeoJSON already uses.
  if (/CRS\s*:?\s*84|OGC:1\.3:CRS84/i.test(identifier)) return true;
  // WKT with no PROJCS is a geographic system.
  return /GEOGCS/i.test(identifier) && !/PROJCS/i.test(identifier);
}

/**
 * Builds the conversion for a CRS, whatever form it was given in.
 *
 * Never throws: an import should report what it could not do, not fail on a
 * stack trace from a projection library.
 */
export function resolveCrs(identifier: string | undefined | null): CrsResolution {
  if (!identifier || !identifier.trim()) {
    return { convert: null, name: 'WGS84 (assumed)' };
  }

  const text = identifier.trim();

  if (isGeographic(text)) {
    return { convert: null, name: describeCrs(text) };
  }

  // Full WKT — what a .prj holds. proj4 reads ESRI and OGC forms directly.
  const looksLikeWkt = /PROJCS|GEOGCS|PROJCRS/i.test(text);
  const code = looksLikeWkt ? null : epsgCodeFrom(text);

  let definition: string | null = null;
  if (looksLikeWkt) {
    definition = text;
  } else if (code !== null) {
    definition = definitionForEpsg(code);
    if (!definition) {
      return {
        convert: null,
        name: `EPSG:${code}`,
        error:
          `EPSG:${code} is not one this app can resolve on its own. ` +
          'Export the data as WGS84 (EPSG:4326), or as a shapefile whose .prj ' +
          'carries the full projection definition.',
      };
    }
  } else {
    return {
      convert: null,
      name: text.slice(0, 60),
      error: 'the coordinate system could not be recognised',
    };
  }

  try {
    const transform = proj4(definition, 'WGS84');
    if (!transform) {
      return { convert: null, name: describeCrs(text), error: 'proj4 returned no transform' };
    }

    /* Proved on one coordinate before a whole file is pushed through it. A
       transform that builds but throws on use would otherwise fail deep in the
       geometry loop, where the error says nothing about the projection. */
    const probe = transform.forward([0, 0]);
    if (!Array.isArray(probe) || !Number.isFinite(probe[0]) || !Number.isFinite(probe[1])) {
      return {
        convert: null,
        name: describeCrs(text),
        error: 'the transform produced no usable result',
      };
    }

    return {
      convert: (x, y) => {
        const [longitude, latitude] = transform.forward([x, y]);
        return [longitude, latitude];
      },
      name: describeCrs(text),
    };
  } catch (error) {
    return {
      convert: null,
      name: describeCrs(text),
      error: error instanceof Error ? error.message : 'the definition could not be read',
    };
  }
}

/** A short human name for a CRS, for notices and error messages. */
export function describeCrs(identifier: string): string {
  const wktName =
    identifier.match(/PROJCS\s*\[\s*"([^"]+)"/i)?.[1] ??
    identifier.match(/GEOGCS\s*\[\s*"([^"]+)"/i)?.[1];
  if (wktName) return wktName;
  const code = epsgCodeFrom(identifier);
  return code !== null ? `EPSG:${code}` : identifier.slice(0, 60);
}

/**
 * True when a datum other than WGS84 is named with no shift parameters.
 *
 * proj4 still converts it, through the ellipsoid, but the result can be a few
 * hundred metres out. Worth a notice rather than a refusal: someone viewing a
 * layer usually wants it on screen, and being told is what lets them decide
 * whether that error matters for what they are doing.
 */
export function needsDatumShift(identifier: string | undefined | null): boolean {
  if (!identifier || /TOWGS84|\+towgs84/i.test(identifier)) return false;
  const datum = identifier.match(/DATUM\s*\[\s*"([^"]+)"/i)?.[1] ?? '';
  return Boolean(datum) && !/WGS[_ ]?(19)?84/i.test(datum);
}

/** Rewrites every coordinate of a geometry in place. */
export function reprojectGeometry(geometry: GeoJSON.Geometry | null, convert: Converter): void {
  if (!geometry) return;
  const walk = (value: any): void => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      const [longitude, latitude] = convert(value[0], value[1]);
      value[0] = longitude;
      value[1] = latitude;
      return;
    }
    for (const part of value) walk(part);
  };
  walk((geometry as any).coordinates);
}

/**
 * Whether a set of features is plausibly lon/lat.
 *
 * The last line of defence: a file with no CRS at all, or one whose CRS was
 * wrong, shows up as coordinates no degree can hold. Better to say the
 * projection could not be applied than to draw the layer somewhere
 * meaningless and let someone believe it.
 */
export function looksProjected(features: GeoJSON.Feature[]): boolean {
  for (const feature of features) {
    let value: any = (feature.geometry as any)?.coordinates;
    while (Array.isArray(value) && Array.isArray(value[0])) value = value[0];
    if (Array.isArray(value) && typeof value[0] === 'number') {
      return Math.abs(value[0]) > 180 || Math.abs(value[1]) > 90;
    }
  }
  return false;
}
